/**
 * EntityLinkCard — single entity link with confirm/reject actions.
 *
 * A13-N11. AppPolish Sprint 4: added ContextLink for tappable entity refs,
 * token colors, 44px touch targets.
 *
 * Props:
 *  - link: { id, target_type, target_id, link_type, confidence, text_span, source_method, user_confirmed }
 *  - onConfirm: () => void
 *  - onReject: () => void
 */

import { useTranslation } from 'react-i18next';
import ContextLink from '../ContextLink';

const METHOD_LABELS = {
  ner_auto: 'brainDump.linkMethod.nerAuto',
  user_manual: 'brainDump.linkMethod.manual',
  wikilink_syntax: 'brainDump.linkMethod.wikilink',
  gps_proximity: 'brainDump.linkMethod.gps',
};

export default function EntityLinkCard({ link, onConfirm, onReject }) {
  const { t } = useTranslation();
  const methodKey = METHOD_LABELS[link.source_method];
  const methodLabel = methodKey ? t(methodKey, link.source_method) : link.source_method;
  const conf = link.confidence ? `${Math.round(link.confidence * 100)}%` : '';

  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition ${
        link.user_confirmed
          ? 'bg-[var(--aif-color-success-light)] border-[var(--aif-color-success-border)]'
          : 'bg-[var(--aif-bg-surface)] border-[var(--aif-border-default)]'
      }`}
      data-testid={`link-card-${link.id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          <ContextLink
            type={link.target_type}
            id={link.target_id}
            label={link.text_span || link.target_type}
            className="text-sm"
          />
          <div className="text-xs text-[var(--aif-text-muted)]">
            {methodLabel} {conf && `· ${conf}`}
          </div>
        </div>
      </div>

      {!link.user_confirmed && (
        <div className="flex gap-1 shrink-0">
          <button
            onClick={onConfirm}
            aria-label={t('brainDump.confirmLink', 'Verknüpfung bestätigen')}
            className="px-2 py-1 bg-[var(--aif-color-success)] hover:opacity-90 text-white text-sm rounded transition min-h-[var(--aif-touch-min)] min-w-[var(--aif-touch-min)]"
          >
            ✓
          </button>
          <button
            onClick={onReject}
            aria-label={t('brainDump.rejectLink', 'Verknüpfung ablehnen')}
            className="px-2 py-1 bg-[var(--aif-bg-surface)] hover:bg-[var(--aif-color-danger)] hover:text-white text-[var(--aif-text-muted)] text-sm rounded border border-[var(--aif-border-default)] transition min-h-[var(--aif-touch-min)] min-w-[var(--aif-touch-min)]"
          >
            ✗
          </button>
        </div>
      )}

      {link.user_confirmed && (
        <span className="text-xs text-[var(--aif-color-success)] font-medium">
          ✓ {t('brainDump.confirmed', 'Bestätigt')}
        </span>
      )}
    </div>
  );
}
